function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamcheckoutButton <iam-add-to-cart-button>
// Extends: cartFormElement
// ----------------------------------------------------------------------------
// Manage the state and behavior of the "Add to Cart" button in response to
// product form interactions.
// - Dynamically updates the button's text, availability, and loading states
//   based on product variant selection, quantity changes, form validation, and
//   the add-to-cart process.
// ----------------------------------------------------------------------------
// Event Listeners:
// - `cartForm:variant:changed`    → Updates the button state when the variant changes
// - `cartForm:quantity:changed`   → Updates the button state when the quantity changes
// - `cartForm:validation:changed` → Disables the button if form validation fails,
//                                      regardless of variant availability
// - `cartForm:submitting`         → Updates the button state to "Adding to Cart"
//                                      while the request is being processed
// - `cartForm:submitted:success`  → Temporarily updates the button state to
//                                      "Added to Cart" after an item is successfully
//                                      added to the cart
// - `cartForm:submitted:error`    → Updates the button state to an "Error" state
//                                      if the add-to-cart request fails. If the
//                                      error is due to the item being sold out,
//                                      the button state is set to "Sold Out", otherwise
//                                      it defaults to a generic error state.
// ----------------------------------------------------------------------------
// Gotchas:
// - Form validation can override button availability, keeping it disabled even
//   if a variant is available.
// - After a successful add-to-cart event, the button remains in the "Item Added"
//   state for ITEM_ADDED_DURATION (default 3 secs) before resetting.
// - If an unknown button state occurs, it defaults to a generic error state and
//   disables the button (until the variant selection changes).
// - Translation strings are defined in the `products.add_to_cart` namespace of
//   the locales file.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import translate from 'scripts/helpers/iamota-translate';
import iamFormElement from 'scripts/classes/iamFormElement';

// -------------------------------------
// Web Component
// -------------------------------------

class iamCheckoutButton extends iamFormElement {
  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // State
    this.state = iamCheckoutButton.BUTTON_STATES.LOADING;

    // Bind Event Listeners
    iamotaEventHandler.subscribe('cartForm:changing', this.onCartChanging.bind(this), {
      source: "checkout-button.js"
    });
    iamotaEventHandler.subscribe('cartForm:changed', this.onCartChanged.bind(this), {
      source: "checkout-button.js"
    });
    iamotaEventHandler.subscribe('cartForm:validation:changed', this.onValidate.bind(this), {
      source: "checkout-button.js"
    });

    // Update the button state
    this.setState(iamCheckoutButton.BUTTON_STATES.CHECKOUT);
  }
  onValidate(_ref) {
    var {
      isValid
    } = _ref;
    // Validation Fails -> disable the checkout button
    this.setDisabled(!isValid);
  }
  onCartChanged(_ref2) {
    var {
      cart
    } = _ref2;
    this.setState(iamCheckoutButton.BUTTON_STATES.CHECKOUT);
  }
  onCartChanging() {
    this.setState(iamCheckoutButton.BUTTON_STATES.LOADING);
  }

  /**
   * Set the current state and update the UI accordingly
   */
  setState(newState) {
    this.state = newState;
    switch (this.state) {
      case iamCheckoutButton.BUTTON_STATES.CHECKOUT:
        this.updateButton({
          buttonText: translate('Checkout', 'checkoutCTA.checkout', {
            cart: this.iamForm.cart
          }),
          isDisabled: false
        });
        break;
      case iamCheckoutButton.BUTTON_STATES.LOADING:
        this.updateButton({
          buttonText: translate('Loading...', 'checkoutCTA.loading', {
            cart: this.iamForm.cart
          }),
          isDisabled: true,
          isLoading: true
        });
        break;
      case iamCheckoutButton.BUTTON_STATES.ERROR:
        this.updateButton({
          buttonText: translate('Checkout', 'checkoutCTA.checkout', {
            cart: this.iamForm.cart
          }),
          isDisabled: true
        });
        break;
      default:
        console.warn("Unknown checkout-button state: ".concat(this.state));
        this.setState(iamCheckoutButton.BUTTON_STATES.ERROR);
        break;
    }
  }

  /**
   * Update button UI based on the provided options
   */
  updateButton(_ref3) {
    var {
      buttonText = this.control.innerHTML,
      isDisabled = false,
      isLoading = false
    } = _ref3;
    this.setText(buttonText);
    this.setLoading(isLoading);
    this.setDisabled(isDisabled, 'updateButton');
  }

  /**
   * Set the button text
   * @param {string} label
  */
  setText(buttonText) {
    var _this = this;
    return _asyncToGenerator(function* () {
      buttonText = yield iamotaEventHandler.applyFilters('cartForm:checkoutButton:buttonText', buttonText);
      _this.control.innerHTML = buttonText;
      _this.control.setAttribute('aria-label', buttonText);
    })();
  }

  /**
   * Disable the button
   * @param {boolean} isDisabled
   */
  setDisabled(isDisabled) {
    var _this$iamForm$validat;
    // Force disabled state if the form is invalid
    var isFormValid = (_this$iamForm$validat = this.iamForm.validator) === null || _this$iamForm$validat === void 0 ? void 0 : _this$iamForm$validat.isFormValid();
    var newState = isDisabled || !isFormValid;
    super.setDisabled(newState);
  }
}
// Button States
_defineProperty(iamCheckoutButton, "BUTTON_STATES", {
  CHECKOUT: 'checkout',
  INVALID: 'invalid',
  LOADING: 'loading',
  ERROR: 'error'
});
customElements.define('iam-checkout-button', iamCheckoutButton);